import React, { createContext, useContext, useReducer, type ReactNode } from 'react';
import type { Song, SetList } from '../types';

// ---- State ----

interface AppState {
  songs: Song[];
  setLists: SetList[];
  loading: boolean;
  error: string | null;
}

const initialState: AppState = {
  songs: [],
  setLists: [],
  loading: false,
  error: null,
};

// ---- Actions ----

type Action =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_SONGS'; payload: Song[] }
  | { type: 'ADD_SONG'; payload: Song }
  | { type: 'UPDATE_SONG'; payload: Song }
  | { type: 'REMOVE_SONG'; payload: string }
  | { type: 'SET_SETLISTS'; payload: SetList[] }
  | { type: 'ADD_SETLIST'; payload: SetList }
  | { type: 'UPDATE_SETLIST'; payload: SetList }
  | { type: 'REMOVE_SETLIST'; payload: string };

function appReducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_SONGS':
      return { ...state, songs: action.payload };
    case 'ADD_SONG':
      return { ...state, songs: [...state.songs, action.payload] };
    case 'UPDATE_SONG':
      return {
        ...state,
        songs: state.songs.map((s) =>
          s.songId === action.payload.songId ? action.payload : s,
        ),
      };
    case 'REMOVE_SONG':
      return {
        ...state,
        songs: state.songs.filter((s) => s.songId !== action.payload),
      };
    case 'SET_SETLISTS':
      return { ...state, setLists: action.payload };
    case 'ADD_SETLIST':
      return { ...state, setLists: [...state.setLists, action.payload] };
    case 'UPDATE_SETLIST':
      return {
        ...state,
        setLists: state.setLists.map((sl) =>
          sl.setListId === action.payload.setListId ? action.payload : sl,
        ),
      };
    case 'REMOVE_SETLIST':
      return {
        ...state,
        setLists: state.setLists.filter((sl) => sl.setListId !== action.payload),
      };
    default:
      return state;
  }
}

// ---- Context ----

interface AppContextValue {
  state: AppState;
  dispatch: React.Dispatch<Action>;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

export function useAppContext(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return ctx;
}
